import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { copy } from '~/eds/src/utils/copy';
import { EntityType } from '~/enums/EntityType';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

import { usePollModelProgress } from '../hooks/usePollModelProgress';
import { DebugModelByDocumentId } from './DocId';
import { DebugModelDocumentsInScope } from './DocsInScope';
import { DebugModelOverview } from './Overview';

export const DebugModel = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { navigate, params } = useRouting();
  const { modelId } = params;

  const {
    progress,
    progressChip,
    isLoading: isLoadingProgress,
  } = usePollModelProgress({
    modelId,
  });

  const { data: model = null, isFetching: isLoadingModel } =
    api.endpoints.getPromptModelPublish.useQuery(
      {
        modelId: modelId!,
      },
      {
        skip: !modelId,
      },
    );

  const [activeTab, setActiveTab] = useState('overview');

  const latestVersionNumber = Math.max(...(model?.version_numbers ?? [1]));
  const latestVersion = model?.versions?.[latestVersionNumber];
  const configId = latestVersion?.config.internal;

  if (!modelId) return null;

  const tabs = [
    {
      label: intl.formatMessage({
        id: 'CustomAiDebugModel.overviewTab',
        defaultMessage: 'Overview & Metrics',
        description: 'Title for the tab showing the model overview and metrics',
      }),
      panel: (
        <DebugModelOverview
          config={latestVersion?.config ?? null}
          entityType={
            model?.target_entity?.type === 'document_attached_data'
              ? EntityType.DocumentAttachedData
              : model?.target_entity?.type === 'field'
                ? EntityType.Field
                : null
          }
          isLoading={isLoadingModel}
          modelJson={JSON.stringify(model, null, 4)}
          progress={progress}
          datePublished={model?.current_publish?.datetime_published ?? null}
        />
      ),
      value: 'overview',
    },
    {
      label: intl.formatMessage({
        id: 'CustomAiDebugModel.docsInScopeWorkflowPredictionsTab',
        defaultMessage: 'Documents in Scope',
        description:
          'Title for the tab showing debugging details for the document workflow predictions in the model scope',
      }),
      panel: (
        <DebugModelDocumentsInScope
          isLoading={isLoadingModel}
          modelId={modelId}
          onDocumentIdClick={({ documentVersionId, highlights, workflow }) => {
            dispatch(
              slices.xRay.actions.setDocumentViewer({
                documentVersionId,
                highlights,
                activeHighlightId: highlights[0]?.id,
              }),
            );
            dispatch(
              slices.xRayDebug.actions.setActiveWorkflowPrediction(workflow),
            );
            setActiveTab('doc-id');
          }}
        />
      ),
      value: 'docs-in-scope',
    },
    {
      label: intl.formatMessage({
        id: 'CustomAiDebugModel.debugByDocumentTab',
        defaultMessage: 'Debug by Document',
        description:
          'Title for the tab showing debugging details for a particular document',
      }),
      panel: <DebugModelByDocumentId modelId={modelId} />,
      value: 'doc-id',
    },
  ];

  const copyConfigAction = {
    icon: 'clipboard' as const,
    text: intl.formatMessage({
      id: 'CustomAiDebugModel.copyConfigActionLabel',
      defaultMessage: 'Copy Configuration ID',
      description:
        'Text label for the action to copy the model configuration ID',
    }),
    onClick: () => {
      copy(configId);
      toast({
        message: intl.formatMessage({
          id: 'CustomAiDebugModel.copyConfigIdToast',
          defaultMessage: 'Copied',
          description:
            'Success message that the text was copied to the clipboard',
        }),
        status: 'success',
      });
    },
  };

  return (
    <PageLayout
      actions={configId ? [copyConfigAction] : undefined}
      chips={progressChip && !isLoadingProgress ? [progressChip] : undefined}
      loadingContent={{ isLoading: isLoadingProgress || isLoadingModel }}
      nav={{
        actions: [
          {
            text: intl.formatMessage({
              id: 'CustomAiDebugModel.customAiNavBreadcrumb',
              defaultMessage: 'Custom AI',
              description:
                'Label for the breadcrumb navigation link to the Custom AI list page',
            }),
            onClick: () => navigate(RoutePathType.AutomationHubFields),
          },
          {
            text: intl.formatMessage({
              id: 'CustomAiDebugModel.customAiModelDetailsNavBreadcrumb',
              defaultMessage: 'Model Details',
              description:
                'Label for the breadcrumb navigation link to the Custom AI model details page',
            }),
            tooltip: intl.formatMessage(
              {
                id: 'CustomAiDebugModel.modelIdTooltip',
                defaultMessage: 'Model ID: {modelId}',
                description:
                  'The model ID. Example: modelId = 603f7c12-b507-4c3b-a043-880925653bba',
              },
              { modelId },
            ),
            onClick: () =>
              navigate(`${RoutePathType.AutomationHubFields}/${modelId}`),
          },
          {
            text: intl.formatMessage({
              id: 'CustomAiDebugModel.customAiDebugModelNavBreadcrumb',
              defaultMessage: 'Debug Model',
              description:
                'Label for the breadcrumb navigation link to the Custom AI model debug page',
            }),
          },
        ],
      }}
      tabs={{
        tabs,
        selectedTab: activeTab,
        onSelectTab: (updatedTabValue) => setActiveTab(updatedTabValue),
      }}
      title={intl.formatMessage({
        id: 'CustomAiDebugModel.pageTitle',
        defaultMessage: 'Debug Custom AI Model',
        description: 'Title for the Custom AI Model Debug page',
      })}
    />
  );
};
